import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { profileApi } from '../../services/backendApi';
import { Card, CardBody, Avatar, Spinner, Alert, Button } from '../../components/ui';
import { DirectoryFilterBar } from '../../components/DirectoryFilterBar';
import { SitePill } from '../../components';
import {
  getFieldOfWorkLabel,
  siteDisplayName,
  buildPublicPassportUrl,
  buildPublicPassportCsv,
  downloadBlob,
  employeeQrFileSlug,
  generatePassportQrPng,
} from '../../utils';
import { useTenants } from '../../contexts/TenantContext';
import { Profile, WorkerPublicPage } from '../../types';

const STATUS_DOT: Record<string, string> = {
  green: 'bg-green-400',
  blue: 'bg-sky-400',
  amber: 'bg-amber-400',
  red: 'bg-red-400',
};

const STATUS_LABEL: Record<string, string> = {
  green: 'Complete',
  blue: 'Awaiting verification',
  amber: 'In Progress',
  red: 'Getting Started',
};

export const SearchWorkers: React.FC = () => {
  const { currentTenant, currentTenantId, tenants } = useTenants();
  const [employees, setEmployees] = useState<Profile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [fieldFilter, setFieldFilter] = useState('');
  const [siteId, setSiteId] = useState('');
  const [groupBySite, setGroupBySite] = useState(false);
  const [exporting, setExporting] = useState<'csv' | 'zip' | null>(null);
  const [exportNote, setExportNote] = useState<string | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchEmployees = async (name?: string, fieldOfWork?: string, site?: string) => {
    setLoading(true);
    setError(null);
    try {
      const result = await profileApi.getWorkers({
        name: name || undefined,
        fieldOfWork: fieldOfWork || undefined,
        siteId: site || undefined,
        limit: 100,
      });
      setEmployees(result ?? []);
    } catch {
      setError('Failed to load employees');
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchEmployees();
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchEmployees(search, fieldFilter, siteId);
    }, 350);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [search, fieldFilter, siteId]);

  const tenantName = currentTenant?.name ?? 'Your organisation';
  const completeCount = employees.filter(employee => employee.passport_status === 'green').length;
  const attentionCount = employees.filter(employee => employee.passport_status !== 'green').length;
  const hasActiveFilters = Boolean(search || fieldFilter || siteId);

  const grouped = useMemo(() => {
    if (!groupBySite) return null;
    const map = new Map<string, Profile[]>();
    for (const worker of employees) {
      const key = worker.tenant_site_id || 'none';
      const list = map.get(key) ?? [];
      list.push(worker);
      map.set(key, list);
    }
    const entries = [...map.entries()].sort(([a], [b]) => {
      if (a === 'none') return 1;
      if (b === 'none') return -1;
      return siteDisplayName(tenants, currentTenantId, a).localeCompare(
        siteDisplayName(tenants, currentTenantId, b),
      );
    });
    return entries;
  }, [employees, groupBySite, tenants, currentTenantId]);

  const loadPublicPages = async (): Promise<WorkerPublicPage[]> => {
    return profileApi.getWorkerPublicPages();
  };

  const pagesToRows = (pages: WorkerPublicPage[]) =>
    pages.map(page => ({
      name: [page.first_name, page.last_name].filter(Boolean).join(' ') || 'Unnamed employee',
      email: page.contact_email ?? '',
      url: buildPublicPassportUrl(page.token),
      visible: page.public_visible,
    }));

  const hiddenNote = (pages: WorkerPublicPage[]) => {
    const hiddenCount = pages.filter(page => !page.public_visible).length;
    if (hiddenCount === 0) return null;
    return `${hiddenCount} profile${hiddenCount === 1 ? ' is' : 's are'} hidden. Those QR codes still work — they open an explainer until the employee makes the page visible.`;
  };

  const handleExportCsv = async () => {
    setExporting('csv');
    setExportNote(null);
    try {
      const pages = await loadPublicPages();
      const csv = buildPublicPassportCsv(pagesToRows(pages));
      downloadBlob(new Blob([csv], { type: 'text/csv;charset=utf-8' }), 'passport-links.csv');
      const note = hiddenNote(pages);
      if (note) setExportNote(note);
    } catch {
      setError('Failed to export Passport links');
    }
    setExporting(null);
  };

  const handleExportZip = async () => {
    setExporting('zip');
    setExportNote(null);
    try {
      const pages = await loadPublicPages();
      const JSZip = (await import('jszip')).default;
      const zip = new JSZip();
      zip.file('passport-links.csv', buildPublicPassportCsv(pagesToRows(pages)));

      for (const page of pages) {
        const url = buildPublicPassportUrl(page.token);
        const dataUrl = await generatePassportQrPng(url, 1024);
        const base64 = dataUrl.split(',')[1];
        const filename = `${employeeQrFileSlug(page.first_name, page.last_name, page.token)}.png`;
        zip.file(filename, base64, { base64: true });
      }

      const blob = await zip.generateAsync({ type: 'blob' });
      downloadBlob(blob, 'passport-qr-pack.zip');
      const note = hiddenNote(pages);
      if (note) setExportNote(note);
    } catch {
      setError('Failed to export QR pack');
    }
    setExporting(null);
  };

  return (
    <div className="space-y-6">
      <div className="bg-white border border-brand-border rounded-lg p-4 md:p-5 flex flex-col lg:flex-row lg:items-center gap-4">
        <div className="flex-1">
          <p className="text-xs text-brand-700 uppercase tracking-wide font-semibold">{tenantName}</p>
          <h1 className="text-2xl font-bold text-gray-900 mt-1">Employees</h1>
          <p className="text-sm text-gray-500 mt-0.5">View and manage employee Passport records</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
          <Button
            variant="secondary"
            size="sm"
            loading={exporting === 'csv'}
            disabled={!!exporting}
            onClick={handleExportCsv}
          >
            Download links CSV
          </Button>
          <Button
            variant="primary"
            size="sm"
            loading={exporting === 'zip'}
            disabled={!!exporting}
            onClick={handleExportZip}
          >
            Download QR pack
          </Button>
        </div>
        <div className="grid grid-cols-3 gap-2 sm:gap-3">
          {[
            { label: 'Total', value: employees.length },
            { label: 'Complete', value: completeCount },
            { label: 'Attention', value: attentionCount },
          ].map(item => (
            <div key={item.label} className="min-w-20 rounded-md border border-brand-border bg-canvas px-3 py-2 text-center">
              <p className="text-lg font-bold text-gray-900">{item.value}</p>
              <p className="text-[10px] uppercase tracking-wide text-gray-500">{item.label}</p>
            </div>
          ))}
        </div>
      </div>

      {error && (
        <Alert variant="danger" onClose={() => setError(null)}>{error}</Alert>
      )}
      {exportNote && (
        <Alert variant="info" onClose={() => setExportNote(null)}>{exportNote}</Alert>
      )}

      <DirectoryFilterBar
        search={search}
        onSearch={setSearch}
        fieldFilter={fieldFilter}
        onFieldFilter={setFieldFilter}
        tenantId={currentTenantId ?? ''}
        siteId={siteId}
        onSiteId={setSiteId}
        groupBySite={groupBySite}
        onGroupBySite={setGroupBySite}
        resultLabel={`${employees.length} employee${employees.length === 1 ? '' : 's'}${siteId ? ` · ${siteDisplayName(tenants, currentTenantId, siteId)}` : ''}`}
        onClear={() => { setSearch(''); setFieldFilter(''); setSiteId(''); }}
        hasActiveFilters={hasActiveFilters}
      />

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Spinner size="lg" />
        </div>
      ) : employees.length > 0 ? (
        <WorkerTable
          employees={employees}
          grouped={grouped}
          tenantId={currentTenantId}
          linkPrefix="/manager/workers"
        />
      ) : (
        <Card>
          <CardBody>
            <div className="text-center py-12">
              <p className="text-gray-500 font-medium">
                {hasActiveFilters ? 'No employees match your search' : 'No employees registered yet'}
              </p>
              {hasActiveFilters && (
                <button
                  onClick={() => { setSearch(''); setFieldFilter(''); setSiteId(''); }}
                  className="text-sm text-brand-600 mt-2 hover:text-brand-700"
                >
                  Clear filters
                </button>
              )}
            </div>
          </CardBody>
        </Card>
      )}
    </div>
  );
};

export const WorkerTable: React.FC<{
  employees: Profile[];
  grouped: [string, Profile[]][] | null;
  tenantId?: string | null;
  showTenant?: boolean;
  linkPrefix: string;
  actionLabel?: string;
}> = ({ employees, grouped, tenantId, showTenant = false, linkPrefix, actionLabel = 'View Passport' }) => {
  const rows = grouped ?? [['all', employees] as [string, Profile[]]];
  return (
    <div className="bg-white border border-brand-border rounded-lg overflow-hidden">
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead className="bg-brand-100 text-brand-900 text-[11px] uppercase tracking-wide">
            <tr>
              <th className="px-4 py-3 font-semibold">Employee</th>
              {showTenant && <th className="px-4 py-3 font-semibold">Tenant</th>}
              <th className="px-4 py-3 font-semibold">Site</th>
              <th className="px-4 py-3 font-semibold">Field of work</th>
              <th className="px-4 py-3 font-semibold">Passport status</th>
              <th className="px-4 py-3 font-semibold text-right">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-brand-border">
            {rows.map(([siteKey, list]) => (
              <React.Fragment key={siteKey}>
                {grouped && (
                  <tr className="bg-canvas sticky top-0">
                    <td colSpan={showTenant ? 6 : 5} className="px-4 py-2">
                      <div className="flex items-center gap-2">
                        <SitePill tenantId={list[0]?.tenant_id ?? tenantId} siteId={siteKey === 'none' ? null : siteKey} />
                        <span className="text-xs text-gray-500">{list.length} workers</span>
                      </div>
                    </td>
                  </tr>
                )}
                {list.map(profile => {
                  const name = [profile.first_name, profile.last_name].filter(Boolean).join(' ') || 'Anonymous Employee';
                  return (
                    <tr key={profile.user_id} className="hover:bg-brand-50/60 transition-colors">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <Avatar src={profile.photo_url} alt={name} size="sm" fallback={profile.first_name?.charAt(0) || '?'} />
                          <span className="font-semibold text-gray-900">{name}</span>
                        </div>
                      </td>
                      {showTenant && (
                        <td className="px-4 py-3 text-gray-500 text-xs">
                          <SitePill tenantId={profile.tenant_id} siteId={profile.tenant_site_id} showTenant />
                        </td>
                      )}
                      <td className="px-4 py-3">
                        <SitePill tenantId={profile.tenant_id ?? tenantId} siteId={profile.tenant_site_id} />
                      </td>
                      <td className="px-4 py-3 text-gray-600">
                        {profile.field_of_work ? getFieldOfWorkLabel(profile.field_of_work) : '—'}
                      </td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center gap-2 text-gray-700">
                          <span className={`w-2 h-2 rounded-full ${STATUS_DOT[profile.passport_status ?? ''] ?? 'bg-gray-300'}`} />
                          {STATUS_LABEL[profile.passport_status ?? ''] ?? 'Unknown'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Link to={`${linkPrefix}/${profile.id}`} className="text-brand-700 font-semibold hover:text-brand-900">
                          {actionLabel}
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
      <div className="md:hidden divide-y divide-brand-border">
        {employees.map(profile => {
          const name = [profile.first_name, profile.last_name].filter(Boolean).join(' ') || 'Anonymous Employee';
          return (
            <Link key={profile.user_id} to={`${linkPrefix}/${profile.id}`} className="flex items-center gap-4 px-4 py-3.5 hover:bg-brand-50">
              <Avatar src={profile.photo_url} alt={name} size="md" fallback={profile.first_name?.charAt(0) || '?'} />
              <div className="flex-1 min-w-0">
                <p className="font-medium text-gray-900 break-words">{name}</p>
                <div className="mt-1">
                  <SitePill tenantId={profile.tenant_id} siteId={profile.tenant_site_id} showTenant={showTenant} />
                </div>
              </div>
              <span className={`w-2.5 h-2.5 rounded-full ${STATUS_DOT[profile.passport_status ?? ''] ?? 'bg-gray-300'}`} />
            </Link>
          );
        })}
      </div>
    </div>
  );
};
